// Top-level reactive app state — Svelte 5 runes mode.
//
// Architecture:
//   * `mode` — UI affordance: "single" (1 tab visible) or "multi" (tab bar visible).
//   * `tabs` — array of KeyTab objects; each represents ONE ShopAIKey
//     workspace with its OWN files / running jobs / usage / history.
//   * `activeTabId` — which tab is currently rendered in the workspace area.
//
// Each KeyTab has its own SSE streams + isolated state, so jobs in
// tab A don't bleed into tab B. The Chrome-tab analogy is literal:
// switch tab, see different state; close tab, that key's session ends.

import { browser } from "../env.js";

// --- Persistence keys for localStorage round-trips -----------------------
const LS_MODE       = "tte:mode";
const LS_OPEN_TABS  = "tte:openTabs";
const LS_ACTIVE_TAB = "tte:activeTab";

function _loadFromLS(key, fallback) {
  if (!browser) return fallback;
  try {
    const raw = localStorage.getItem(key);
    return raw === null ? fallback : JSON.parse(raw);
  } catch {
    return fallback;
  }
}

function _saveToLS(key, value) {
  if (!browser) return;
  try { localStorage.setItem(key, JSON.stringify(value)); }
  catch { /* quota exceeded — silently drop */ }
}

// --- Tab factory --------------------------------------------------------

/**
 * Build a fresh KeyTab. `keyEntry` is the public payload from
 * /api/admin/keys (id, label, masked_key, balance, ...) or null when
 * we're seeding a "default" tab for the .env key on first run.
 */
// Defaults pulled from a SHARED localStorage cache so a brand-new tab
// inherits the user's last-used preferences (one-time copy at tab creation
// — once the tab exists, its config is isolated and edits don't bleed to
// other tabs). Stored under "tte:newTabDefaults" as JSON.
function _loadNewTabDefaults() {
  if (typeof localStorage === "undefined") return null;
  try {
    const raw = localStorage.getItem("tte:newTabDefaults");
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}
export function saveNewTabDefaults(snapshot) {
  if (typeof localStorage === "undefined") return;
  try { localStorage.setItem("tte:newTabDefaults", JSON.stringify(snapshot)); }
  catch { /* quota */ }
}

// Migrate stored stages snapshots from the pre-split schema. Users who had
// localStorage["tte:newTabDefaults"] set BEFORE the icons-sheet split kept
// `themepack.gen_icons: true/false`. Map it to the new sheet-1 / sheet-2
// pair so their saved preference survives the rename — defaulting both
// new toggles to whatever the legacy single toggle was set to.
function _migrateLegacyStages(stages) {
  if (!stages || typeof stages !== "object") return stages;
  const tp = stages.themepack;
  if (tp && typeof tp === "object" && "gen_icons" in tp) {
    const legacy = !!tp.gen_icons;
    if (!("gen_icons_sheet_1" in tp)) tp.gen_icons_sheet_1 = legacy;
    if (!("gen_icons_sheet_2" in tp)) tp.gen_icons_sheet_2 = legacy;
    delete tp.gen_icons;
  }
  return stages;
}

export function createTab(keyEntry) {
  const isDefault = keyEntry == null;
  const dflt = _loadNewTabDefaults() || {};
  // Plain object — deep reactivity is inherited from the parent `tabs`
  // array which IS wrapped in $state. Svelte's Proxy reactivity covers
  // nested fields automatically (any access to tab.someField gets
  // tracked by the active component).
  return {
    // Identity
    keyId:      isDefault ? "default" : keyEntry.id,
    label:      isDefault ? "Default (.env)" : keyEntry.label,
    maskedKey:  isDefault ? "(.env)" : keyEntry.masked_key,
    isDefault,
    // Which inner panel is active in the workspace
    activePanel: "generator", // "generator" | "usage" | "history"
    // --- Generator config: PER-TAB so switching tabs preserves each
    // tab's choices. Defaults to user's last-used preferences (copied
    // from newTabDefaults snapshot at tab creation), or to spec defaults
    // if no snapshot yet.
    pipelineTab:      dflt.pipelineTab      ?? "themepack",
    detectionBackend: dflt.detectionBackend ?? "api",
    detectionModel:   dflt.detectionModel   ?? "gemini-2.5-flash",
    inpaintModel:     dflt.inpaintModel     ?? "gemini-2.5-flash-image",
    generationModel:  dflt.generationModel  ?? "gpt-image-1-all",
    genStrategy:      dflt.genStrategy      ?? "sequential",
    chatProvider:     dflt.chatProvider     ?? "shopaikey",
    imageEndpoint:    dflt.imageEndpoint    ?? "images_edits",
    chatPassword:     "",  // intentionally NEVER persisted (security)
    // Per-pipeline stage toggles. Each pipeline has its own set;
    // toggling on one pipeline doesn't affect the others. Defaults
    // mirror server-side Form defaults (all on = full pipeline run).
    stages: dflt.stages ? _migrateLegacyStages(JSON.parse(JSON.stringify(dflt.stages))) : {
      themepack: {
        gen_background: true,
        gen_icons_sheet_1: true,
        gen_icons_sheet_2: true,
        gen_widgets: true,
        extract_icons: true,
        extract_widgets: true,
      },
      control_center: {
        gen_background: true, gen_surfaces: true,
        gen_pills_decor: true, gen_toggle_icons: true,
      },
      keyboard: {
        gen_kb_background: true, gen_kb_sheet: true,
      },
      // CC preview is single-shot — no per-stage toggles. Kept as an
      // empty object so the stages panel just shows a helpful hint.
      cc_preview: {},
    },
    // CC preview per-tab state — secondary character upload + text inputs
    // for the media widget + decoration letter row. Only used when
    // pipelineTab === "cc_preview".
    ccPreviewCharacter: [],
    ccPreviewCharacterPreviews: [],
    // Empty defaults = "auto" mode. Backend detects empty → prompt tells
    // the model to pick a themed word matching the wallpaper mood. User
    // can type over to lock a specific value.
    ccPreviewLabelText:  dflt.ccPreviewLabelText  ?? "",
    ccPreviewTrackTitle: dflt.ccPreviewTrackTitle ?? "",
    ccPreviewArtistName: dflt.ccPreviewArtistName ?? "",
    // Other per-tab runtime state
    files:      [],          // File[]
    filePreviews: [],         // string[] of object URLs
    padding: dflt.padding ?? 5,
    // Active job in THIS tab (1 file = 1 job; no batch mode within a tab
    // because each tab is already a "key channel" for parallelism)
    job:        null,         // server JobState
    jobId:      null,
    view:       "idle",       // idle | submitting | tracking | succeeded | failed | cancelled
    error:      null,
    eventSource: null,        // EventSource for live updates — set when tracking
    startedAt:  null,         // unix seconds — set at submit, drives elapsed timer
                              //   (lives on tab so the counter survives tab switches)
    // Usage snapshot — cached from /api/admin/keys (refresh per-tab on demand)
    usage:      keyEntry ? {
      balance:    keyEntry.balance,
      used:       keyEntry.used,
      total:      keyEntry.total,
      name:       keyEntry.name,
      group_name: keyEntry.group_name,
      used_pct:   keyEntry.used_pct,
    } : null,
    // History panel state
    historyPage: 1,
    historyData: null,
    historyLoading: false,
    historyError: null,
  };
}

// --- The global stores --------------------------------------------------

// Mode toggle — single vs multi. Persisted so reload restores user preference.
let _mode = $state(_loadFromLS(LS_MODE, "single"));
export function getMode() { return _mode; }
export function setMode(next) {
  if (next !== "single" && next !== "multi") return;
  _mode = next;
  _saveToLS(LS_MODE, next);
}

// Active tabs — created lazily as the user adds keys. Initial state is
// a single "default" tab pointing at the .env key. When the keys API
// returns, we reconcile: replace default with the first stored key in
// single mode, or open multiple tabs if the user previously saved them.
export const tabs = $state([createTab(null)]);

// Which tab is rendered in the workspace
let _activeTabId = $state(_loadFromLS(LS_ACTIVE_TAB, "default"));
export function getActiveTabId() { return _activeTabId; }
export function setActiveTabId(id) {
  if (tabs.some((t) => t.keyId === id)) {
    _activeTabId = id;
    _saveToLS(LS_ACTIVE_TAB, id);
  }
}

// Convenience: the active tab object (or null if none open).
export function getActiveTab() {
  return tabs.find((t) => t.keyId === _activeTabId) || tabs[0] || null;
}

// --- Tab CRUD -----------------------------------------------------------

export function openTabForKey(keyEntry) {
  // If a tab for this key already exists, just activate it.
  const existing = tabs.find((t) => t.keyId === keyEntry.id);
  if (existing) {
    setActiveTabId(existing.keyId);
    return existing;
  }
  // Otherwise, append a new tab and activate it.
  const tab = createTab(keyEntry);
  tabs.push(tab);
  setActiveTabId(tab.keyId);
  _persistOpenTabs();
  return tab;
}

export function closeTab(tabId) {
  const idx = tabs.findIndex((t) => t.keyId === tabId);
  if (idx === -1) return;
  // Don't allow closing the last tab — at least one must stay visible.
  if (tabs.length === 1) return;
  // Tear down SSE if active
  const tab = tabs[idx];
  if (tab.eventSource) {
    try { tab.eventSource.close(); } catch { /* */ }
  }
  // Revoke any object URLs to release memory
  for (const u of tab.filePreviews) URL.revokeObjectURL(u);
  tabs.splice(idx, 1);
  // Activate the tab to the left (or the new last if we removed last)
  if (_activeTabId === tabId) {
    const newActive = tabs[Math.max(0, idx - 1)] || tabs[0];
    if (newActive) setActiveTabId(newActive.keyId);
  }
  _persistOpenTabs();
}

function _persistOpenTabs() {
  // Save the open tab IDs so reload restores the same workspace.
  _saveToLS(LS_OPEN_TABS, tabs.map((t) => t.keyId));
}

// --- SSE attach/detach helpers ------------------------------------------
//
// Lives in the store (not in GeneratorPanel) so JobsModal can re-attach
// SSE when loading a running job from history — the modal is rendered
// AS AN OVERLAY above the generator panel, so the panel never unmounts
// and its onMount-based reattach wouldn't fire. By centralizing here,
// any caller can attach/detach SSE for a tab.
export function openJobSse(tab, jobId) {
  if (!tab || !jobId) return;
  closeJobSse(tab);
  const es = new EventSource(`/api/jobs/${jobId}/events`);
  es.addEventListener("state", (ev) => {
    let s;
    try { s = JSON.parse(ev.data); } catch { return; }
    tab.job = s;
    if (s.stage === "done")             { tab.view = "succeeded"; }
    else if (s.stage === "error")       { tab.view = "failed"; tab.error = s.error || "Unknown error"; }
    else if (s.stage === "cancelled")   { tab.view = "cancelled"; }
    else if (s.stage === "interrupted") { tab.view = "failed"; tab.error = s.error || s.message || "Bị gián đoạn"; }
    else                                 { tab.view = "tracking"; }
  });
  es.addEventListener("done", () => { closeJobSse(tab); });
  es.onerror = () => {
    if (es.readyState === EventSource.CLOSED) {
      console.warn(`SSE for ${jobId} closed permanently`);
    }
  };
  tab.eventSource = es;
}

export function closeJobSse(tab) {
  if (tab?.eventSource) {
    try { tab.eventSource.close(); } catch { /* */ }
    tab.eventSource = null;
  }
}

// Restore from localStorage on app boot. Called by App.svelte after
// the keys list has been fetched (so we know which IDs are still valid).
export function reconcileTabsFromKeys(keysList) {
  const savedIds = _loadFromLS(LS_OPEN_TABS, []);
  if (!Array.isArray(savedIds) || savedIds.length === 0) return;
  // For each saved id, open a tab if the key still exists.
  for (const id of savedIds) {
    if (id === "default") continue; // default tab is always there
    const k = keysList.find((x) => x.id === id);
    if (k && !tabs.some((t) => t.keyId === id)) {
      tabs.push(createTab(k));
    }
  }
  // Restore the active tab too — but only if it's actually open now.
  if (savedIds.includes(_activeTabId) || _activeTabId === "default") {
    // already set from LS at module init; no-op
  }
}
